import React from 'react'
import { WorkPeriodEvent } from '../../api/work-period-events'
import { useCreateMutation, useUpdateMutation } from '../../queries/work-period-events'
import { Field, Form, Formik } from 'formik'
import InputField from '../form/fields/input'
import { RequestButton, RequestMessage } from '../request-components/request-components'
import SelectField from '../form/fields/select'
import locales from '../../../../locales/work-period-events'
import { Columns, Element, Icon, Message } from 'react-bulma-components'
import { HelpIcon } from '../icons'
import WorkPeriodEventInformativeDescription from './work-period-event-informative-description'
import WorkPeriodEventBenefitDescription from './work-period-event-benefit-description'
import CheckboxField from '../form/fields/checkbox'
import WorkPeriodEventIndicatesAbsenceDescription from './work-period-event-indicates-absence-description'
import { isAuthorizedFor } from '../protected/protected'

export interface WorkPeriodEventFormProps {
  workPeriodEvent?: WorkPeriodEvent
}

const WorkPeriodEventForm: React.FC<WorkPeriodEventFormProps> = ({ workPeriodEvent }) => {
  const createMutation = useCreateMutation()
  const updateMutation = useUpdateMutation()
  const isSuperAdmin = isAuthorizedFor(['superAdmin'])
  const isEditMode = Boolean(workPeriodEvent)
  const canEdit = !isEditMode || isSuperAdmin

  return (
    <Formik
      initialValues={workPeriodEvent || { code: '', title: '', type: '' }}
      enableReinitialize
      validate={values => {
        const errors: any = {}
        if (!values.code) errors.code = 'Champ requis'
        if (!values.title) errors.title = 'Champ requis'
        if (!values.type) errors.type = 'Champ requis'
        return errors
      }}
      onSubmit={values => {
        if (!canEdit) return
        workPeriodEvent
          ? updateMutation.mutate({ ...values, id: workPeriodEvent._id.toString() })
          : createMutation.mutate(values)
      }}
    >
      {() => {
        return (
          <Form autoComplete="off">
            <Field label="Code" name="code" component={InputField} disabled={!canEdit} />
            <Field label="Intitulé" name="title" component={InputField} disabled={!canEdit} />
            <Columns>
              <Columns.Column size={6}>
                <Field
                  label="Type"
                  name="type"
                  component={SelectField}
                  options={[
                    {
                      value: 'informative',
                      label: locales.types.informative,
                    },
                    {
                      value: 'benefit',
                      label: locales.types.benefit,
                    },
                  ]}
                  disabled={!canEdit}
                />
              </Columns.Column>
              <Columns.Column size={6}>
                <Field
                  label="Indique absence"
                  name="indicatesAbsence"
                  component={CheckboxField}
                  disabled={!canEdit}
                />
              </Columns.Column>
            </Columns>
            {!canEdit && (
              <Element renderAs="p" textSize={7} textColor="grey" mb={2}>
                Pour toute modification des évènements, veuillez en faire la demande à
                support@teamtim.fr
              </Element>
            )}
            <Message color={'info'} textSize={7}>
              <Message.Body>
                <Icon>
                  <HelpIcon />
                </Icon>
                <Element>
                  <WorkPeriodEventInformativeDescription />
                </Element>
                <Element>
                  <WorkPeriodEventBenefitDescription />
                </Element>
                <Element>
                  <WorkPeriodEventIndicatesAbsenceDescription />
                </Element>
              </Message.Body>
            </Message>
            <RequestButton
              color="primary"
              type="submit"
              mutation={workPeriodEvent ? updateMutation : createMutation}
              data-test="save-work-period-events"
              disabled={!canEdit}
            >
              {workPeriodEvent ? "Mettre à jour l'Évènement" : 'Nouvel Évènement'}
            </RequestButton>
            <RequestMessage mutation={workPeriodEvent ? updateMutation : createMutation} />
          </Form>
        )
      }}
    </Formik>
  )
}

export default WorkPeriodEventForm
